import { CSSProperties, useCallback, useRef, useState } from 'react';

import IconPlayButtonBlackOutline from '@backrs_ui/common-ui/src/assets/images/play_button_black_outline.svg';
import { COLOR_TOKENS } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { RADIUS } from '@backrs_ui/common-ui/src/designSystem/spacing/spacing';

import { Box, SxProps, Theme } from '@mui/material';

export type VideoUploadProps = Readonly<{
  mode: 'edit' | 'view';
  videoUrl: string;
  onVideoChange?: (file: File) => void;
  roundedCorners?: boolean;
}>;

export const VideoUpload = ({
  mode,
  videoUrl,
  onVideoChange,
  roundedCorners = true,
}: VideoUploadProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePlayClick = useCallback(() => {
    if (mode === 'edit') {
      fileInputRef.current?.click();
    } else {
      setIsPlaying((prev) => !prev);
    }
  }, [mode]);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      if (file && onVideoChange) {
        onVideoChange(file);
      }
    },
    [onVideoChange],
  );

  const handleVideoEnd = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const showOverlay = mode === 'edit' || (mode === 'view' && !isPlaying);

  return (
    <Box
      className="profile-video"
      position="relative"
      onClick={mode === 'edit' ? handlePlayClick : undefined}
    >
      {showOverlay && (
        <Box
          component="img"
          sx={playButtonContainerStyle}
          onClick={handlePlayClick}
          src={IconPlayButtonBlackOutline}
          width={56}
          height={56}
        />
      )}
      <Box
        sx={{
          borderRadius: roundedCorners ? RADIUS['radius-2xl'] : '0',
          ...videoPlayerStyle,
        }}
      >
        <video
          width="100%"
          height="100%"
          src={videoUrl}
          style={htmlVideoStyle}
          controls={isPlaying}
          onEnded={handleVideoEnd}
        />
      </Box>
      {mode === 'edit' && (
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="video/*"
          style={{ display: 'none' }}
        />
      )}
    </Box>
  );
};

const videoPlayerStyle: SxProps<Theme> = {
  overflow: 'hidden',
  width: '100%',
  aspectRatio: '16 / 9',
};

const playButtonContainerStyle: SxProps<Theme> = {
  position: 'absolute',
  bottom: '12%',
  left: '5%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  zIndex: 1,
};

const htmlVideoStyle: CSSProperties = {
  objectFit: 'contain',
  backgroundColor: COLOR_TOKENS['Colors/Base/Black'],
};
